/**
 * Aday Kişisel Davet — `/sinav/davet/:token` (S6+ admin model)
 *
 * Admin tarafından önceden yaratılmış kişisel davet linki.
 * Preview gösterilir, tek tıkla sınav başlar (self-register yok).
 */
import { useEffect, useState } from 'react';
import { useLocation, useParams } from 'wouter';
import { Header } from '../components/Header.js';
import { Alert, Button, Card } from '../components/ui.js';
import { api, ApiError } from '../lib/api.js';

interface InvitationPreview {
  candidate: {
    fullName: string | null;
    position: string | null;
    experienceYears: string;
  };
  organization: { name: string; industry: string };
  exam: {
    questionCount: number;
    durationMin: number;
    anonymousMode: boolean;
    validUntil: string;
  };
  attempt: {
    id: string;
    status: 'IN_PROGRESS' | 'COMPLETED' | 'EXPIRED' | 'ABANDONED';
    totalScore: number | null;
    scoreLevel: string | null;
  } | null;
}

interface StartResult {
  attemptId: string;
  resumed: boolean;
  durationMin: number;
  totalQuestions: number;
  expiresAt: string;
}

const INDUSTRY_LABELS: Record<string, string> = {
  MACHINERY: 'Makine İmalat',
  AUTOMOTIVE: 'Otomotiv',
  DEFENSE: 'Savunma Sanayi',
  CONSUMER: 'Tüketim Ürünleri',
  SHEET_FAB: 'Sac Metal Fabrikasyon',
  PRECISION_MECHANICAL: 'Hassas Mekanik',
  OTHER: 'Diğer',
};

const EXP_LABELS: Record<string, string> = {
  LT_1Y: '<1 yıl',
  Y1_TO_2: '1-2 yıl',
  Y2_TO_5: '2-5 yıl',
  GT_5Y: '5+ yıl',
};

export function ExamInvitationPage(): JSX.Element {
  const [, setLocation] = useLocation();
  const params = useParams<{ token: string }>();
  const token = params.token ?? '';
  const [preview, setPreview] = useState<InvitationPreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('Geçersiz davet linki');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await api.get<InvitationPreview>(
          `/api/public/sinav-davet/${token}`,
        );
        if (cancelled) return;
        setPreview(data);
        // Tamamlanmışsa direkt sonuç sayfasına
        if (data.attempt?.status === 'COMPLETED') {
          setLocation(`/sinav/sonuc/${data.attempt.id}`);
        }
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError) setError(err.message);
        else setError('Davet yüklenemedi');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, setLocation]);

  async function startExam(): Promise<void> {
    setStarting(true);
    setError(null);
    try {
      const data = await api.post<StartResult>(
        `/api/public/sinav-davet/${token}/start`,
      );
      setLocation(`/sinav/oturum/${data.attemptId}`);
    } catch (err) {
      setStarting(false);
      if (err instanceof ApiError) setError(err.message);
      else setError('Sınav başlatılamadı');
    }
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <main className="mx-auto max-w-md px-6 py-12">
          <Card>
            <Alert variant="danger">{error}</Alert>
            <p className="mt-4 text-sm text-slate-600">
              Davet linki geçersiz veya süresi dolmuş olabilir. Sizi davet
              eden firmaya ulaşın.
            </p>
          </Card>
        </main>
      </div>
    );
  }

  if (!preview) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <main className="mx-auto max-w-md px-6 py-12">
          <p className="text-slate-500">Davet yükleniyor…</p>
        </main>
      </div>
    );
  }

  const validUntil = new Date(preview.exam.validUntil);
  const isResume = preview.attempt?.status === 'IN_PROGRESS';

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <main className="mx-auto max-w-lg px-6 py-12">
        <Card>
          <h1 className="text-2xl font-bold text-slate-900">
            {isResume ? 'Sınavınıza Devam Edin' : 'Sınava Davet'}
          </h1>
          {preview.candidate.fullName && (
            <p className="mt-2 text-base text-slate-700">
              Merhaba <strong>{preview.candidate.fullName}</strong>,
            </p>
          )}
          <p className="mt-1 text-sm text-slate-600">
            <strong>{preview.organization.name}</strong> firması adına BMU
            SkillProbe SolidWorks yetkinlik değerlendirmesine davetlisiniz.
          </p>

          <dl className="mt-6 space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm">
            <Row label="Firma" value={preview.organization.name} />
            <Row
              label="Sektör"
              value={
                INDUSTRY_LABELS[preview.organization.industry] ??
                preview.organization.industry
              }
            />
            <Row
              label="Soru Sayısı"
              value={`${preview.exam.questionCount} soru`}
            />
            <Row label="Süre" value={`${preview.exam.durationMin} dakika`} />
            {preview.candidate.position && (
              <Row label="Pozisyon" value={preview.candidate.position} />
            )}
            <Row
              label="Deneyim"
              value={
                EXP_LABELS[preview.candidate.experienceYears] ??
                preview.candidate.experienceYears
              }
            />
            <Row
              label="Geçerlilik"
              value={validUntil.toLocaleDateString('tr-TR', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            />
          </dl>

          <div className="mt-6 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
            <strong>Önemli:</strong> "Sınava Başla" butonuna tıkladığınızda
            geri sayım anında başlar. Sınav süresince sekme değiştirmeyin.
            Cevaplarınız otomatik kaydedilir, bağlantı kesilse bile kaldığınız
            yerden devam edebilirsiniz.
          </div>

          <Button
            onClick={startExam}
            loading={starting}
            className="mt-6 w-full"
            size="lg"
          >
            {isResume ? 'Sınava Devam Et' : 'Sınava Başla'}
          </Button>

          <p className="mt-3 text-center text-xs text-slate-400">
            BMU Works © 2026 — bmuworks.com
          </p>
        </Card>
      </main>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="flex items-center justify-between border-b border-slate-200 pb-2 last:border-0 last:pb-0">
      <dt className="text-slate-600">{label}</dt>
      <dd className="font-medium text-slate-900">{value}</dd>
    </div>
  );
}
